import React, { useState, useEffect, useRef } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Asset, Role, environments, Status, AttributeSensitivity, isLinkableUrl } from '../helpers';
import {
  authenticationService,
  settingService,
  displayNameService,
  statusService,
  attributeSensitivityService,
  userSettingValueService,
} from '../services';
import { PrivateRoute } from '../middleware';
import {
  Breadcrumbs,
  ContextMenu,
  EnvironmentLabel,
  Icon,
  Message,
  ModalDialog,
  SearchBar,
  Photo,
  Tooltip,
  Link,
  NavLink,
  Page,
  SideDialog,
  UserSettings,
} from '../components';
import { routesConfig } from '../routesConfig';
import { ChangePassword, Login, LoadApp, NotFound, ForgotPassword, ResetPassword } from './';
import packageJson from '../../package.json';
import { useGlobalState, useMessage, useModalDialog } from '../hooks';

export const App = () => {
  return (
    <div className="App-wrapper">
      <_App />
      <SideDialog />
      <ModalDialog />
      <Message />
      <Tooltip />
      <ContextMenu />
    </div>
  );
};

const _App = () => {
  const [loading, setLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [isLoggedIn, setIsLoggedIn] = useGlobalState('isLoggedIn');
  const [csrfToken, setCsrfToken] = useGlobalState('csrfToken');
  const [settings, setSettings] = useGlobalState('settings');
  const [userSettings, setUserSettings] = useGlobalState('userSettings');
  const [displayNames, setDisplayNames] = useGlobalState('displayNames');
  const [statuses, setStatuses] = useGlobalState('statuses');
  const [attributeSensitivities, setAttributeSensitivities] =
    useGlobalState('attributeSensitivities');

  const [showNavMenu, setShowNavMenu] = useState(true);

  const { showMessage, hideMessage } = useMessage();
  const { show } = useModalDialog();

  const location = useLocation();
  const navigate = useNavigate();

  // Store settings in ref to access them later
  const settingsRef = useRef();

  useEffect(() => {
    // Initialize app
    initialize();
  }, []);

  useEffect(() => {
    // Get user settings
    getUserSettings();

    // Get statuses
    getStatuses();
  }, [loggedInUser]);

  const initialize = async () => {
    try {
      //Promise.all([
      // Get settings from backend
      await getSettings().catch(e => {
        throw e;
      });
      //  ,
      // Get login state
      await getLoginState().catch(e => {
        throw e;
      });
      //  ,
      // Get display names from backend
      await getDisplayNames().catch(e => {
        throw e;
      });

      // Get Nav menu state from local storage
      setShowNavMenu(localStorage.getItem('showNavMenu') === 'false' ? false : true);

      setLoading(false);
    } catch (err) {
      setLoading(false);
      showMessage(err, 'error', true);
    }
  };

  const getSettings = async () => {
    try {
      let settings = await settingService.getAllForFrontend();

      settings = settings.settings;
      settings = {
        ...settings,
        authMethods: [
          ...(settings.OIDC_IS_ENABLED ? ['oidc'] : []),
          ...(settings.LOCAL_IS_ENABLED ? ['local'] : []),
        ],
      };
      setSettings({ ...settingsRef.current, ...settings });
      settingsRef.current = settings;
    } catch (err) {
      throw err;
    }
  };

  const getUserSettings = async () => {
    try {
      // Only for logged in users
      if (!(isLoggedIn && loggedInUser)) return;
      let settings = await userSettingValueService.getByUserId(loggedInUser.user_id).catch(e => {
        throw e;
      });

      settings = settings.user_setting_values;
      settings = {
        ...settings,
      };

      setUserSettings(settings);
    } catch (err) {
      // Log error silently
      console.error(err);
      //showMessage(err, 'error', true)
    }
  };

  const getDisplayNames = async () => {
    try {
      let result = await displayNameService.getAllForFrontend();

      result = result.display_names;

      const obj = result.reduce((accumulator, value) => {
        return {
          ...accumulator,
          [value.display_name_code]: {
            display_name: value.display_name,
            display_name_alternative_name: value.display_name_alternative_name,
            display_name_description: value.display_name_description,
            display_name_instruction: value.display_name_instruction,
          },
        };
      }, {});

      setDisplayNames(obj);
    } catch (err) {
      throw err;
    }
  };

  const getStatuses = async () => {
    try {
      // Only for logged in users
      if (!(isLoggedIn && loggedInUser)) return;

      let result = await statusService.getAll();

      result = result.statuses;

      let obj = result.reduce((accumulator, value) => {
        return {
          ...accumulator,
          [value.status_id]: {
            label: value.status_name,
            tooltip: value.status_description,
            className: Status[value.status_stage]?.className,
            ...value,
          },
        };
      }, {});

      // Set status flags in settings
      setSettings({
        ...settingsRef.current,
        SHOW_GLOSSARY_STATUSES:
          Object.values(obj).filter(x => x.status_asset_category === Asset.Glossary).length > 1,
        SHOW_SYSTEM_STATUSES:
          Object.values(obj).filter(x => x.status_asset_category === Asset.System).length > 1,
      });

      // Append raw data
      obj.raw = result;
      obj.default_glossary_status_id = result.find(
        x => x.status_asset_category === Asset.Glossary && x.status_is_default
      )?.status_id;
      obj.default_system_status_id = result.find(
        x => x.status_asset_category === Asset.System && x.status_is_default
      )?.status_id;

      // Set statuses
      setStatuses(obj);
    } catch (err) {
      // Log error silently
      console.error(err);
    }
  };

  const getAttributeSensitivities = async () => {
    try {
      let result = await attributeSensitivityService.getAll();

      result = result.attribute_sensitivities;

      const obj = result.reduce((accumulator, value) => {
        return {
          ...accumulator,
          [value.attribute_sensitivity_id]: {
            display_name: value.display_name,
            display_name_alternative_name: value.display_name_alternative_name,
            display_name_description: value.display_name_description,
          },
        };
      }, {});

      setAttributeSensitivities(obj);
    } catch (err) {
      throw err;
    }
  };

  const getLoginState = async () => {
    try {
      const loginState = await authenticationService.endLogin(window.location.href, undefined);

      if (loginState.userInfo) {
        setLoggedInUser(loginState.userInfo);
      }

      if (loginState.csrf) {
        setCsrfToken(loginState.csrf);
      }

      if (loginState.loginHandled) {
        const state = JSON.parse(loginState.state);

        // Restore url
        //navigate({...state.from, ...state})
        navigate({ pathname: '/', ...state.from, state: { ...state?.from?.state, ...state } });
      }

      setIsLoggedIn(loginState.isLoggedIn);
    } catch (err) {
      throw err;
    }
  };

  const login = async ({ username, password, state }) => {
    try {
      const loginState = await authenticationService.login(username, password);

      if (loginState.userInfo) {
        setLoggedInUser(loginState.userInfo);
      }

      if (loginState.csrf) {
        setCsrfToken(loginState.csrf);
      }

      if (loginState.loginHandled) {
        setIsLoggedIn(loginState.isLoggedIn);
        hideMessage();

        // Restore url
        navigate({
          pathname: '/',
          ...state.from,
          state: { ...state?.from?.state, ...state },
          replace: true,
        });
      }
    } catch (e) {
      // Errors handled in Login.js
      throw e;
    }
  };

  const logout = async () => {
    try {
      const logout = await authenticationService.logout();

      const tmpUser = loggedInUser;
      setIsLoggedIn(false);
      setLoggedInUser({});

      hideMessage();

      if (tmpUser.user_auth_method !== 'local') {
        location.href = logout.logoutURL;
      }
    } catch (e) {
      showMessage(e, 'error');
    }
  };

  const toggleNavMenu = () => {
    const state = !showNavMenu;

    setShowNavMenu(state);

    // Persist state in local storage
    localStorage.setItem('showNavMenu', state.toString());
  };

  const navMenuState = showNavMenu ? 'show' : '';

  const aboutMetadata = () => {
    let metadata = [];

    // Version
    metadata.push({ property: 'Version', value: packageJson.version });

    // Environment
    metadata.push({
      property: 'Environment',
      value: settings?.ENVIRONMENT
        ? environments.find(x => x.value === settings?.ENVIRONMENT).value.toUpperCase()
        : 'Undefined',
    });

    // Organization
    metadata.push({
      property: 'Organization',
      value: settings?.ORGANIZATION_NAME ? settings.ORGANIZATION_NAME : 'Undefined',
    });

    return metadata;
  };
  return (
    <div className={'App' + (show ? ' filter-blur' : '')}>
      {isLoggedIn &&
        !(location.pathname === '/change_password' || location.pathname === '/login') &&
        !loading && (
          <div className={'navbar ' + navMenuState}>
            <div className="navbar-header">
              <Link to="/">
                <Icon name="katalogue_small" tooltip="Go to start page" />
                <div className="logo-text">katalogue</div>
              </Link>

              <EnvironmentLabel />
            </div>

            <EnvironmentLabel className="navbar-hidden" />

            <div className="navbar-content">
              {/*<div className="navbar-group">
              <NavLink end to="/" className={ ({ isActive }) => "nav-link home" + (isActive ? " active" : "") } tooltip="Home"><Icon name="home"/><span>Home</span></NavLink>
            </div>*/}

              <div className="navbar-group">
                <div className="navbar-group-title grey-text">
                  <span>BROWSE</span>
                </div>
                <NavLink
                  to="/systems"
                  className={({ isActive }) => 'nav-link datasets' + (isActive ? ' active' : '')}
                  tooltip="Browse Datasets"
                >
                  <Icon name="datasets" />
                  <span>Datasets</span>
                </NavLink>
                <NavLink
                  to="/glossaries"
                  className={({ isActive }) => 'nav-link glossaries' + (isActive ? ' active' : '')}
                  tooltip="Browse Glossaries"
                >
                  <Icon name="glossaries" />
                  <span>Glossaries</span>
                </NavLink>
              </div>

              {loggedInUser && loggedInUser.user_role_name === Role.admin && (
                <div className="navbar-group">
                  <div className="navbar-group-title grey-text">
                    <span>MANAGE</span>
                  </div>
                  <NavLink
                    to="/connections"
                    className={({ isActive }) =>
                      'nav-link connections' + (isActive ? ' active' : '')
                    }
                    tooltip="Manage Connections"
                  >
                    <Icon name="connections" />
                    <span>Connections</span>
                  </NavLink>
                  <NavLink
                    to="/tasks"
                    className={({ isActive }) => 'nav-link tasks' + (isActive ? ' active' : '')}
                    tooltip="Manage Tasks"
                  >
                    <Icon name="tasks" />
                    <span>Tasks</span>
                  </NavLink>
                  <NavLink
                    to="/schedules"
                    className={({ isActive }) => 'nav-link schedules' + (isActive ? ' active' : '')}
                    tooltip="Manage Schedules"
                  >
                    <Icon name="schedules" />
                    <span>Schedules</span>
                  </NavLink>
                  <NavLink
                    to="/custom_properties"
                    className={({ isActive }) =>
                      'nav-link custom_properties' + (isActive ? ' active' : '')
                    }
                    tooltip="Manage Custom Properties"
                  >
                    <Icon name="custom_properties" />
                    <span>Custom Properties</span>
                  </NavLink>
                  <NavLink
                    to="/users"
                    className={({ isActive }) => 'nav-link users' + (isActive ? ' active' : '')}
                    tooltip="Manage Users"
                  >
                    <Icon name="users" />
                    <span>Users</span>
                  </NavLink>
                  <NavLink
                    to="/user_groups"
                    className={({ isActive }) =>
                      'nav-link user_groups' + (isActive ? ' active' : '')
                    }
                    tooltip="Manage User Groups"
                  >
                    <Icon name="user_groups" />
                    <span>User Groups</span>
                  </NavLink>
                  <NavLink
                    to="/settings"
                    className={({ isActive }) => 'nav-link settings' + (isActive ? ' active' : '')}
                    tooltip="Manage Settings"
                  >
                    <Icon name="settings" />
                    <span>Settings</span>
                  </NavLink>
                </div>
              )}
            </div>
            <div className="navbar-footer">
              <div
                className="navbar-toggle-button"
                onClick={toggleNavMenu}
                title={(showNavMenu ? 'Minimize' : 'Expand') + ' navigation menu'}
              >
                <span></span>
              </div>
            </div>
          </div>
        )}
      <div className="main-container">
        {isLoggedIn &&
          !(location.pathname === '/change_password' || location.pathname === '/login') &&
          !loading && (
            <div className="main-content-header-wrap">
              <div className={'main-content-header' + (location.pathname === '/' ? ' home' : '')}>
                <Breadcrumbs />

                <div className="logo"></div>

                <div className="menu">
                  {location.pathname !== '/' && (
                    <div className="menu-item">
                      <div className="menu-button">
                        <SearchBar />
                      </div>
                    </div>
                  )}
                  <div className="menu-item">
                    <div className="menu-button">
                      <Icon name="about" />
                    </div>
                    <div className="menu-content">
                      <div className="about-body">
                        <div className="about-logo">
                          <Icon name="katalogue" />
                        </div>
                        <div className="about-section">
                          {(settings?.ORGANIZATION_SUPPORT_URL ||
                            settings?.ORGANIZATION_RESOURCE_URL) && (
                            <React.Fragment>
                              {settings?.ORGANIZATION_RESOURCE_URL &&
                                isLinkableUrl(settings.ORGANIZATION_RESOURCE_URL) && (
                                  <Link
                                    className="menu-link"
                                    to={settings.ORGANIZATION_RESOURCE_URL}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    tooltip={`Visit ${settings?.ORGANIZATION_NAME || "your organization's"} internal Katalogue resources`}
                                  >
                                    <Icon name="internal_resource" />
                                    <span className="external-link">
                                      {settings?.ORGANIZATION_RESOURCE_NAME ||
                                        settings.ORGANIZATION_RESOURCE_URL}
                                    </span>
                                  </Link>
                                )}
                              {settings?.ORGANIZATION_SUPPORT_URL &&
                                // An email address links as mailto:; anything else must be a
                                // linkable http(s) URL, so a path or other scheme is not linked.
                                ((settings.ORGANIZATION_SUPPORT_URL.includes('@') &&
                                  !settings.ORGANIZATION_SUPPORT_URL.includes('://')) ||
                                  isLinkableUrl(settings.ORGANIZATION_SUPPORT_URL)) && (
                                  <Link
                                    className="menu-link"
                                    to={
                                      settings.ORGANIZATION_SUPPORT_URL.includes('@') &&
                                      !settings.ORGANIZATION_SUPPORT_URL.includes('://')
                                        ? `mailto:${settings.ORGANIZATION_SUPPORT_URL}`
                                        : settings.ORGANIZATION_SUPPORT_URL
                                    }
                                    {...(settings.ORGANIZATION_SUPPORT_URL.includes('@') &&
                                    !settings.ORGANIZATION_SUPPORT_URL.includes('://')
                                      ? {}
                                      : { target: '_blank', rel: 'noopener noreferrer' })}
                                    tooltip={`Contact ${settings?.ORGANIZATION_NAME || "your organization's"} ${settings?.ORGANIZATION_SUPPORT_NAME || 'support'} to get help with Katalogue `}
                                  >
                                    <Icon name="support" />
                                    <span className="external-link">
                                      Contact {settings?.ORGANIZATION_SUPPORT_NAME || 'Support'}
                                    </span>
                                  </Link>
                                )}
                            </React.Fragment>
                          )}

                          <Link
                            className="menu-link"
                            to={'https://docs.katalogue.se'}
                            rel="noopener noreferrer"
                            target="_blank"
                            tooltip="The official Katalogue documentation"
                          >
                            <Icon name="docs" />
                            <span className="external-link">Katalogue Docs</span>
                          </Link>
                          {settings?.REST_API_IS_ENABLED && (
                            <Link
                              className="menu-link"
                              to={settings?.REST_API_DOCS_URL ?? ''}
                              rel="noopener noreferrer"
                              target="_blank"
                              tooltip="Katalogue REST API reference"
                            >
                              <Icon name="api_docs" />
                              <span className="external-link">API Documentation</span>
                            </Link>
                          )}
                        </div>
                      </div>

                      <hr />

                      <div className="about-metadata">
                        {aboutMetadata().map((x, i) => {
                          return (
                            <div className="about-metadata-row">
                              <span className="about-metadata-property">{x.property}</span>
                              <span className="about-metadata-value">{x.value}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div className="about-copyright">
                        © {new Date().getFullYear()}{' '}
                        <a href="http://www.kayenta.se" rel="noopener noreferrer" target="_blank">
                          Kayenta Consulting AB
                        </a>
                      </div>
                    </div>
                  </div>

                  <div className="menu-item last">
                    <div className="menu-button">
                      <Link className="menu-link" to={'/profile'}>
                        <span className="user-name">
                          <Photo
                            size="small"
                            data={loggedInUser.user_photo}
                            name={loggedInUser.user_fullname}
                            loading={loading}
                          />
                        </span>
                      </Link>
                    </div>

                    <div className="menu-content">
                      <div className="user-profile">
                        <Photo
                          size="medium"
                          data={loggedInUser.user_photo}
                          name={loggedInUser.user_fullname}
                          loading={loading}
                        />
                        <div className="user-profile-info">
                          <div className="user-profile-name">{loggedInUser.user_fullname}</div>
                          <div className="user-profile-username">{loggedInUser.user_username}</div>
                          {loggedInUser.user_title && (
                            <div className="user-profile-title">{loggedInUser.user_title}</div>
                          )}
                          {loggedInUser.user_department && (
                            <div className="user-profile-title">{loggedInUser.user_department}</div>
                          )}
                        </div>
                      </div>
                      <div className="user-profile-actions">
                        <Link className="menu-link" to={'/profile'}>
                          <Icon name="profile" />
                          <span>Profile</span>
                        </Link>
                        {loggedInUser.user_auth_method === 'local' && (
                          <Link className="menu-link" to={'/change_password'}>
                            <Icon name="password" />
                            <span>Change Password</span>
                          </Link>
                        )}
                        <div className="menu-link" onClick={logout}>
                          <Icon name="signout" />
                          <span>Sign Out</span>
                        </div>
                      </div>
                      <hr />
                      <div className="user-profile-settings">
                        <UserSettings />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        <div
          className={
            'main-content' +
            (['/', '/login', '/forgot_password', '/change_password'].includes(location.pathname) ||
            loading
              ? ' no-bg'
              : '')
          }
        >
          {loading ? (
            <LoadApp />
          ) : (
            <Routes>
              <Route
                path="/login"
                element={<Page title="Login" element={<Login login={login} />} />}
              />

              {settings.MAIL_IS_ENABLED && ( // Only enable these endpoints if mailing is enabled
                <React.Fragment>
                  <Route
                    path="/forgot_password"
                    element={<Page title="Forgot Password" element={<ForgotPassword />} />}
                  />
                  <Route
                    path="/reset_password"
                    element={<Page title="Reset Password" element={<ResetPassword />} />}
                  />
                </React.Fragment>
              )}

              <Route
                path="/change_password"
                element={<Page title="Change Password" element={<ChangePassword />} />}
              />

              {routesConfig.map(({ path, name, element, roles, title, objectName }, key) => (
                <Route
                  key={key}
                  path={path}
                  element={
                    <Page
                      title={title}
                      objectName={objectName}
                      element={<PrivateRoute roles={roles} element={element} />}
                    />
                  }
                />
              ))}

              <Route path="*" element={<Page title="Not Found" element={<NotFound />} />} />
            </Routes>
          )}
        </div>
      </div>
    </div>
  );
};
